import { useEffect, useId, useRef, useState } from 'react'
import { matchBroadcastNames, parseBroadcastNames } from '../domain/broadcast.ts'
import { t } from '../i18n.ts'
import { CONCERTS } from '../program.ts'
import { useStore } from '../store/StoreProvider.tsx'
import './ImportSchedule.css'

type State =
  | { kind: 'idle' }
  | { kind: 'importing' }
  | { kind: 'done'; marked: number; unmatched: string[] }
  | { kind: 'failed'; reason: 'link' | 'missing' | 'fetch' }

const FAILURE_MESSAGE = {
  link: t.broadcast.failedLink,
  missing: t.broadcast.failedMissing,
  fetch: t.broadcast.failedFetch,
} as const

/**
 * Imports a schedule built in Broadcast, from the header.
 *
 * A header action rather than a page: the import is a one-off setup step, done once at
 * the start of the festival and never again, and giving it a route would put a
 * permanent destination in an app with four of them for something nobody visits twice.
 *
 * The marks are written straight into the local store, one `setVote` per concert. That
 * is the same path a tap on a thumb takes, so the import needs no privileges, no
 * sign-in, and no new store API — and a signed-in user's sync layer picks the writes up
 * exactly as it picks up forty taps.
 */
export function ImportSchedule() {
  const store = useStore()
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [state, setState] = useState<State>({ kind: 'idle' })
  const inputId = useId()
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement | null>(null)

  /**
   * Closing empties the panel.
   *
   * The link and its result belong together. A field still holding the last link is
   * the worse half of that: the panel reopens looking ready, and the next paste lands
   * *after* what is already there, producing a URL nobody typed. The result goes for
   * the same reason — a stale "40 konserter fikk tommel opp" over a forgotten link
   * reads as a report on whatever is pasted next.
   *
   * A failed attempt survives, because the panel stays open through a failure: the
   * link is still there to fix and retry.
   */
  function close() {
    setOpen(false)
    setUrl('')
    setState({ kind: 'idle' })
  }

  /*
   * Closing on Escape and on a tap outside.
   *
   * The panel hangs off a sticky header, over the schedule. Without either of these
   * the only way out is the icon it came from — and on a phone, the instinct after
   * changing your mind is to tap the list, not to find the button again.
   */
  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close()
    }
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) close()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setState({ kind: 'importing' })

    let html: string
    try {
      const response = await fetch('/api/import/broadcast', {
        method: 'POST',
        // Broadcast renders on demand and the request crosses our server on the way,
        // so a bad connection can leave this pending indefinitely. Without a deadline
        // the button sits on "Henter planen …" with no error and no way to retry.
        signal: AbortSignal.timeout(15_000),
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      })
      if (response.status === 400) {
        setState({ kind: 'failed', reason: 'link' })
        return
      }
      if (response.status === 404) {
        setState({ kind: 'failed', reason: 'missing' })
        return
      }
      if (!response.ok) {
        setState({ kind: 'failed', reason: 'fetch' })
        return
      }
      html = ((await response.json()) as { html?: string }).html ?? ''
    } catch {
      // Offline, timed out, or the dev server is running without the API. All of them
      // are "we could not read the plan", which is what the message says.
      setState({ kind: 'failed', reason: 'fetch' })
      return
    }

    const names = parseBroadcastNames(html)
    if (names.length === 0) {
      // A page that parsed to nothing is either an empty schedule or a Broadcast
      // redesign. Neither is something the user can fix by retrying, but reporting it
      // as "no concerts on that link" is true in both cases.
      setState({ kind: 'failed', reason: 'missing' })
      return
    }

    const { matched, unmatched } = matchBroadcastNames(CONCERTS, names)
    for (const concert of matched) store.setVote(concert.id, 'up')
    setState({ kind: 'done', marked: matched.length, unmatched })
  }

  return (
    <div className="import" ref={rootRef}>
      <button
        type="button"
        className="app-header__action"
        aria-label={t.nav.import}
        title={t.nav.import}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <ImportIcon />
      </button>

      {open && (
        <div className="import__panel" id={panelId}>
          <form className="import__form" onSubmit={submit} aria-label={t.broadcast.title}>
            <h2 className="import__title">{t.broadcast.title}</h2>
            <p className="import__help">{t.broadcast.help}</p>

            <label className="sr-only" htmlFor={inputId}>
              {t.broadcast.urlLabel}
            </label>
            <input
              id={inputId}
              className="import__input"
              type="url"
              required
              inputMode="url"
              // A pasted URL is not prose, and iOS capitalises and corrects it into
              // something that no longer resolves.
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              autoComplete="off"
              // The panel only exists because the icon was tapped, so the field is
              // unambiguously what was asked for — and on a phone it saves a second tap
              // before the paste.
              autoFocus
              placeholder={t.broadcast.urlPlaceholder}
              value={url}
              onChange={(event) => {
                setUrl(event.target.value)
                if (state.kind !== 'idle') setState({ kind: 'idle' })
              }}
            />

            <div className="import__actions">
              <button
                type="submit"
                className="import__submit"
                disabled={state.kind === 'importing'}
              >
                {state.kind === 'importing' ? t.broadcast.importing : t.broadcast.submit}
              </button>
              <button type="button" className="import__close" onClick={close}>
                {t.broadcast.close}
              </button>
            </div>

            {state.kind === 'done' && (
              <p className="import__status import__status--ok" role="status">
                {t.broadcast.done(state.marked)}
                {state.unmatched.length > 0 && (
                  <span className="import__skipped">{t.broadcast.skipped(state.unmatched)}</span>
                )}
              </p>
            )}

            {state.kind === 'failed' && (
              <p className="import__status import__status--error" role="status">
                {FAILURE_MESSAGE[state.reason]}
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  )
}

function ImportIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* An arrow coming down into a tray: pulling a plan in, next to the inbox tray
          that receives what friends send. */}
      <path
        d="M12 3v10m0 0l-3.5-3.5M12 13l3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 16v2.5A1.5 1.5 0 005.5 20h13a1.5 1.5 0 001.5-1.5V16"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
